/*
  File: App.tsx
  Author: Binish Mookken
  Date: April-June 2024

  Description:
  This file contains the main component App, which serves as the entry point for the application.
  It sets up the routing, authentication context, and theme configuration.

  Components:
  - App: Main component for the application.
  - AppContent: Component for rendering the main content based on routing.

  Props:
  - None

  Dependencies:
  - react: React library for building user interfaces.
  - useState: Hook for managing state in functional components.
  - ConfigProvider: Component from antd for providing configuration to antd components.
  - theme: Theme configuration from antd.
  - Card: Component from antd for rendering a card layout.
  - BrowserRouter: Component from react-router-dom for setting up routing.
  - Routes: Component from react-router-dom for defining routes.
  - Route: Component from react-router-dom for defining individual routes.
  - useLocation: Hook from react-router-dom for accessing the current location.
  - Navigation: Component for rendering the navigation menu.
  - Login: Component for handling user login.
  - AuthProvider: Context provider for managing authentication state.
  - useAuth: Custom hook for accessing authentication context.
  - AgentPage: Component for rendering the agent page.
  - Dashboard: Component for rendering the dashboard page.
  - Landing: Component for rendering the landing page.
  - MyProfilePage: Component for rendering the user's profile page.
  - UsersPage: Component for rendering the users page.

  Functions:
  - handleThemeChange: Function for toggling between dark and light mode.

  Styles:
  - App.css: Contains styles for the App component.

*/

import { useEffect, useState } from "react";
import { ConfigProvider, theme, Card } from "antd";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import Navigation from "./Navigation";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AgentPage from "./AgentPage";
import Dashboard from "./Dashboard";
import Landing from "./Landing";
import MyProfilePage from "./MyProfile";
import UsersPage from "./UsersPage";
import { GoogleOAuthProvider } from "@react-oauth/google";

interface AppContentProps {}

export default function App() {
  const { defaultAlgorithm, darkAlgorithm } = theme;
  const [isDarkMode, setIsDarkMode] = useState(true);

  const handleThemeChange = () => {
    setIsDarkMode((previousValue) => !previousValue);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            colorPrimary: "#045cba",
          },
          Input: {
            colorPrimary: "#045cba",
          },
        },
        token: {
          // colorPrimary: "#045cba",
          colorBgContainer: "#1a1a1a",
          // borderRadius: 2,
        },
        algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
      }}
    >
      <Card style={{ border: "none" }}>
        <BrowserRouter>
          <GoogleOAuthProvider clientId="1067434998710-8sgg3526t7otsbcnjf89uppkvepfocgd.apps.googleusercontent.com">
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </GoogleOAuthProvider>
        </BrowserRouter>
      </Card>
    </ConfigProvider>
  );
}

function AppContent({}: AppContentProps) {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const location = useLocation();
  const shouldRenderNavigation =
    location.pathname !== "/login" && location.pathname !== "/";

  useEffect(() => {
    // Redirect to Landing route if user is not logged in and not already on the Landing page
    if (!isLoggedIn) {
      navigate("/");
    }
  }, [isLoggedIn]);

  return (
    <AuthProvider>
      {shouldRenderNavigation && <Navigation />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/agents" element={<AgentPage />} />
        <Route path="/my-profile" element={<MyProfilePage />} />
        <Route path="/settings" element={<UsersPage />} />
      </Routes>
    </AuthProvider>
  );
}
